import { useState } from "react"
import { Check, ChevronDown, Copy } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Collapsible, CollapsibleTrigger, AnimatedCollapsibleContent } from "@/components/ui/collapsible"
import { ScrollArea } from "@/components/ui/scroll-area"

// HTTP method to badge variant mapping
export const METHOD_VARIANTS = {
  GET: "info",
  POST: "success",
  PUT: "warning",
  PATCH: "warning",
  DELETE: "destructive",
}

// Replace {{field_name}} placeholders in body template with extracted values
export function substituteBodyTemplate(bodyTemplate, extractedData) {
  if (!bodyTemplate || !extractedData?.fields?.length) return bodyTemplate

  const normalize = s => s?.toLowerCase().replace(/[\s_]+/g, " ").trim()

  // Build a normalized-key lookup so whitespace/case differences don't break substitution
  const valueMap = {}
  for (const field of extractedData.fields) {
    if (field.name != null) valueMap[normalize(field.name)] = field.value
  }

  function substitute(obj) {
    if (typeof obj === "string") {
      return obj.replace(/\{\{([^}]+)\}\}/g, (match, name) => {
        const key = normalize(name)
        const val = valueMap[key]
        return (key in valueMap && val != null) ? val : match
      })
    }
    if (Array.isArray(obj)) return obj.map(substitute)
    if (obj && typeof obj === "object") {
      const result = {}
      for (const [key, val] of Object.entries(obj)) {
        result[key] = substitute(val)
      }
      return result
    }
    return obj
  }

  return substitute(bodyTemplate)
}

// Insert <wbr> elements at logical break points in URLs
function BreakableUrl({ url }) {
  // Split URL into segments, keeping delimiters
  const parts = url.split(/([/\-_.?&=])/)

  return (
    <>
      {parts.map((part, i) => (
        <span key={i}>
          {part}
          {/* Add break opportunity after delimiters (except the last part) */}
          {i < parts.length - 1 && /^[/\-_.?&=]$/.test(part) && <wbr />}
        </span>
      ))}
    </>
  )
}

export function RequestPreview({ method = "POST", url, headers = [], body }) {
  const [copied, setCopied] = useState(null)
  const [headersOpen, setHeadersOpen] = useState(true)
  const [bodyOpen, setBodyOpen] = useState(true)

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text)
    setCopied(type)
    setTimeout(() => setCopied(null), 2000)
  }

  const formattedBody = (() => {
    if (!body) return null
    if (typeof body === "string") {
      try { return JSON.stringify(JSON.parse(body), null, 2) } catch { return body }
    }
    return JSON.stringify(body, null, 2)
  })()

  return (
    <div className="rounded-lg border border-border bg-muted/50 overflow-hidden">
      {/* Method + URL */}
      <div className="@container/url px-3 py-2 border-b border-border bg-background">
        <div className="flex items-start gap-2">
          <Badge variant={METHOD_VARIANTS[method] || "success"} size="small" className="shrink-0">
            {method}
          </Badge>
          {url ? (
            <>
              <code className="hidden @xs/url:block flex-1 text-[13px] text-foreground font-mono">
                <BreakableUrl url={url} />
              </code>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 shrink-0 -mt-1 @xs/-mr-1 -mb-1 text-muted-foreground hover:text-accent-foreground"
                    onClick={() => copyToClipboard(url, "url")}
                  >
                    {copied === "url" ? (
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Copy URL</TooltipContent>
              </Tooltip>
            </>
          ) : (
            <code className="flex-1 text-[13px] text-muted-foreground font-mono">
              https://example.com/webhook
            </code>
          )}
        </div>
        {url && (
          <code className="block @xs/url:hidden mt-2 text-[13px] text-foreground font-mono">
            <BreakableUrl url={url} />
          </code>
        )}
      </div>

      {/* Headers */}
      {headers.length > 0 && (
        <Collapsible open={headersOpen} onOpenChange={setHeadersOpen}>
          <CollapsibleTrigger className="group flex items-center justify-between w-full px-3 py-2 border-b border-border bg-background hover:bg-muted transition-colors cursor-pointer">
            <span className="text-[13px] font-medium text-muted-foreground">
              Headers ({headers.length})
            </span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground group-hover:text-foreground transition duration-200 ${headersOpen ? "" : "-rotate-90"}`} />
          </CollapsibleTrigger>
          <AnimatedCollapsibleContent open={headersOpen}>
            <div className="@container/headers">
              <div className="grid @xs/headers:grid-cols-[auto_1fr] text-[13px] font-mono">
                {headers.map(({ key, value }, i) => (
                  <div key={i} className="grid @xs/headers:col-span-2 @xs/headers:grid-cols-subgrid border-t border-border first:border-t-0 last:border-b">
                    <span className="px-3 pt-2 @xs/headers:py-2 text-slate-500 whitespace-nowrap">{key}</span>
                    <span className="px-3 pb-2 @xs/headers:py-2 text-foreground break-all">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedCollapsibleContent>
        </Collapsible>
      )}

      {/* Body */}
      {formattedBody && (
        <Collapsible open={bodyOpen} onOpenChange={setBodyOpen} className="-mb-px">
          <CollapsibleTrigger className="group flex items-center justify-between w-full px-3 py-2 border-b border-border bg-background hover:bg-muted transition-colors cursor-pointer">
            <span className="text-[13px] font-medium text-muted-foreground">
              Body
            </span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground group-hover:text-foreground transition duration-200 ${bodyOpen ? "" : "-rotate-90"}`} />
          </CollapsibleTrigger>
          <AnimatedCollapsibleContent open={bodyOpen}>
            <div className="relative">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-1.5 right-2 h-7 w-7 p-0 z-10 text-muted-foreground hover:text-accent-foreground"
                    onClick={() => copyToClipboard(formattedBody, "body")}
                  >
                    {copied === "body" ? (
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Copy body</TooltipContent>
              </Tooltip>
              <ScrollArea orientation="both" scrollBarClassName="h-2.5 border-b border-b-transparent">
                <pre className="px-3 py-2 text-[13px] font-mono text-foreground">
                  <code>{formattedBody}</code>
                </pre>
              </ScrollArea>
            </div>
          </AnimatedCollapsibleContent>
        </Collapsible>
      )}
    </div>
  )
}
